import React, { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { ContactShadows, Sparkles } from "@react-three/drei";
import * as THREE from "three";

/**
 * CardSnap "Value Orb" — hinged-lid collector sphere.
 *
 * Closed on load → lid hinges BACKWARD around its rear equator edge (like a
 * clamshell), revealing a warm gold interior. Camera sits slightly elevated so
 * we look down into the bowl. The scan UI is overlaid inside the bowl opening.
 *
 * Phases:
 *  - "idle-closed" -> closed, cinematic entrance
 *  - "open"        -> lid hinged back ~110°, interior lit warm gold (endpoint)
 *  - "processing"  -> closed, rapid spin, gem pulses
 *  - "result-open" -> same visual as "open"
 */
export default function Pokeball({ phase = "open" }) {
  return (
    <Canvas
      shadows
      dpr={[1, 2]}
      camera={{ position: [0, 0.55, 5.2], fov: 36 }}
      gl={{ antialias: true, alpha: true }}
      style={{ background: "transparent", pointerEvents: "none" }}
      onCreated={({ camera }) => camera.lookAt(0, -0.15, 0)}
    >
      <Suspense fallback={null}>
        <hemisphereLight args={["#ffffff", "#1a0f12", 0.55]} />
        <ambientLight intensity={0.35} />
        <spotLight
          position={[-6, 8, 6]}
          angle={0.38}
          penumbra={1}
          intensity={3.2}
          color="#ffffff"
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
        />
        <spotLight position={[7, 2.5, 4]} intensity={1.8} color="#ff8591" />
        <spotLight position={[0, -2, 4]} intensity={0.8} color="#7dd3fc" />
        <directionalLight position={[0, 3, 6]} intensity={0.9} color="#fff4e6" />
        {/* Warm interior glow — only lit when bowl is open */}
        <pointLight
          position={[0, -0.2, 0]}
          intensity={phase === "open" || phase === "result-open" ? 5.5 : 0.0}
          color="#ffb347"
          distance={4.5}
        />
        <ValueOrbMesh phase={phase} />
        <Sparkles
          count={60}
          scale={[6, 6, 3]}
          size={2}
          speed={phase === "processing" ? 1.2 : 0.3}
          color="#fbbf24"
          opacity={phase === "processing" ? 0.95 : 0.5}
        />
        <ContactShadows
          position={[0, -1.45, 0]}
          opacity={0.65}
          scale={7}
          blur={2.6}
          far={2.6}
        />
      </Suspense>
    </Canvas>
  );
}

function ValueOrbMesh({ phase }) {
  const groupRef = useRef();
  const topPivotRef = useRef();
  const gemRef = useRef();
  const bowlInnerRef = useRef();
  const rimRef = useRef();

  const mountTimeRef = useRef(null);
  const lastPhaseRef = useRef(phase);

  // Targets per phase
  const targetRef = useRef({
    lidAngle: 0, // radians — how far the lid is hinged back
    gemGlow: 0.4,
    interiorGlow: 0.0,
    rimGlow: 0.4,
    spin: 0.22,
  });

  useEffect(() => {
    if (lastPhaseRef.current !== phase && phase === "idle-closed") {
      mountTimeRef.current = null;
    }
    lastPhaseRef.current = phase;

    if (phase === "idle-closed") {
      targetRef.current = { lidAngle: 0, gemGlow: 0.4, interiorGlow: 0.0, rimGlow: 0.3, spin: 0.22 };
    } else if (phase === "open" || phase === "result-open") {
      // Lid hinged back ~60° so the button still faces forward (matches reference)
      targetRef.current = { lidAngle: -Math.PI * 0.34, gemGlow: 1.6, interiorGlow: 1.6, rimGlow: 2.0, spin: 0.0 };
    } else if (phase === "processing") {
      targetRef.current = { lidAngle: 0, gemGlow: 2.4, interiorGlow: 0.0, rimGlow: 0.6, spin: 7.5 };
    }
  }, [phase]);

  useFrame((state, dt) => {
    if (!groupRef.current) return;

    // Entrance (first 1s after mount)
    if (mountTimeRef.current === null) mountTimeRef.current = state.clock.elapsedTime;
    const tSinceMount = state.clock.elapsedTime - mountTimeRef.current;
    const ENTRANCE = 1.0;
    const inEntrance = tSinceMount < ENTRANCE;

    if (inEntrance) {
      const k = Math.min(1, tSinceMount / ENTRANCE);
      const ease = 1 + (2.2 * Math.pow(k - 1, 3) + 1.6 * Math.pow(k - 1, 2));
      groupRef.current.scale.setScalar(0.15 + 0.95 * ease);
      groupRef.current.rotation.y = -Math.PI * 2.4 * (1 - k);
      groupRef.current.position.y = -0.5 * (1 - k) - 0.1;
    } else {
      groupRef.current.scale.setScalar(
        THREE.MathUtils.lerp(groupRef.current.scale.x, 1.15, dt * 4),
      );
      groupRef.current.rotation.y += targetRef.current.spin * dt;
      const targetY =
        phase === "idle-closed" || phase === "processing"
          ? -0.1 + Math.sin(state.clock.elapsedTime * 1.1) * 0.04
          : -0.1;
      groupRef.current.position.y = THREE.MathUtils.lerp(
        groupRef.current.position.y,
        targetY,
        dt * 3,
      );
    }

    const t = targetRef.current;

    // Lid hinge
    if (topPivotRef.current) {
      topPivotRef.current.rotation.x = THREE.MathUtils.lerp(
        topPivotRef.current.rotation.x,
        t.lidAngle,
        dt * 4.5,
      );
    }

    // Interior glow
    if (bowlInnerRef.current) {
      bowlInnerRef.current.material.emissiveIntensity = THREE.MathUtils.lerp(
        bowlInnerRef.current.material.emissiveIntensity,
        t.interiorGlow,
        dt * 4,
      );
    }

    // Rim glow
    if (rimRef.current) {
      rimRef.current.material.emissiveIntensity = THREE.MathUtils.lerp(
        rimRef.current.material.emissiveIntensity,
        t.rimGlow,
        dt * 4,
      );
    }

    // Gem breathing
    if (gemRef.current) {
      const breath =
        phase === "processing"
          ? Math.abs(Math.sin(state.clock.elapsedTime * 7)) * 0.6
          : Math.sin(state.clock.elapsedTime * 1.8) * 0.15;
      gemRef.current.material.emissiveIntensity = THREE.MathUtils.lerp(
        gemRef.current.material.emissiveIntensity,
        t.gemGlow + breath,
        dt * 4,
      );
    }
  });

  return (
    <group ref={groupRef}>
      {/* ---- HINGED TOP (lid) ---- */}
      {/* Pivot positioned at back equator edge, group inside counter-translates */}
      <group ref={topPivotRef} position={[0, 0, -1]}>
        <group position={[0, 0, 1]}>
          {/* Red lacquered top */}
          <mesh castShadow receiveShadow>
            <sphereGeometry args={[1, 96, 96, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshPhysicalMaterial
              color="#c62530"
              metalness={0.2}
              roughness={0.22}
              clearcoat={1}
              clearcoatRoughness={0.06}
              reflectivity={0.8}
              sheen={0.4}
              sheenColor="#ff7a85"
            />
          </mesh>
          {/* Inside of lid (back-face, warm gold) */}
          <mesh>
            <sphereGeometry args={[0.97, 64, 64, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial
              color="#3a2112"
              emissive="#8b5a2b"
              emissiveIntensity={0.65}
              metalness={0.45}
              roughness={0.5}
              side={THREE.BackSide}
            />
          </mesh>
          {/* Black band under the lid edge */}
          <mesh position={[0, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[1.003, 0.035, 24, 160]} />
            <meshPhysicalMaterial color="#0a0a0a" metalness={0.6} roughness={0.35} />
          </mesh>
          {/* Center button base (sticks up from top front of lid) */}
          <mesh position={[0, 0, 1.0]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.19, 0.2, 0.06, 48]} />
            <meshPhysicalMaterial color="#0a0a0a" metalness={0.8} roughness={0.3} />
          </mesh>
          {/* Center button (ivory/gold dome) */}
          <mesh ref={gemRef} position={[0, 0, 1.03]} rotation={[Math.PI / 2, 0, 0]}>
            <sphereGeometry
              args={[0.14, 48, 48, 0, Math.PI * 2, 0, Math.PI / 2]}
            />
            <meshPhysicalMaterial
              color="#fff3c4"
              emissive="#ffb64c"
              emissiveIntensity={0.6}
              metalness={0.25}
              roughness={0.25}
              clearcoat={1}
              clearcoatRoughness={0.1}
            />
          </mesh>
        </group>
      </group>

      {/* ---- BOTTOM BOWL (static) ---- */}
      <mesh castShadow receiveShadow>
        <sphereGeometry args={[1, 96, 96, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
        <meshPhysicalMaterial
          color="#ede3d0"
          metalness={0.35}
          roughness={0.28}
          clearcoat={1}
          clearcoatRoughness={0.08}
          reflectivity={0.85}
          sheen={0.3}
          sheenColor="#fff4d6"
        />
      </mesh>
      {/* Inner bowl shell — warm gold interior visible when lid is open */}
      <mesh ref={bowlInnerRef}>
        <sphereGeometry args={[0.96, 64, 64, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2]} />
        <meshStandardMaterial
          color="#2a1608"
          emissive="#ffb347"
          emissiveIntensity={0.0}
          metalness={0.6}
          roughness={0.35}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Gold rim around the bowl's mouth (glows when open) */}
      <mesh ref={rimRef} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.005, 0.022, 24, 180]} />
        <meshStandardMaterial
          color="#fbbf24"
          emissive="#f59e0b"
          emissiveIntensity={0.4}
          metalness={1}
          roughness={0.22}
        />
      </mesh>
      {/* Thin accent ring just below */}
      <mesh position={[0, -0.055, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.998, 0.007, 18, 140]} />
        <meshStandardMaterial
          color="#ffd77a"
          emissive="#f59e0b"
          emissiveIntensity={0.3}
          metalness={1}
          roughness={0.2}
        />
      </mesh>
    </group>
  );
}
